/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useState } from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import NodeIcon from '@atlaskit/icon/core/node';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { stripMarkdown } from '../../common/utils/strip-markdown';
import { useCyclingIcon } from '../../controllers/use-cycling-icon';
import { ResearchExpandable } from '../research-expandable';

import type { CompactResearchStep, TraceInteractionEvent } from './CompactResearchSteps';
import { TRACE_INTERACTION_TYPES } from './CompactResearchSteps';
import messages from './messages';
import { ShimmerText } from './ShimmerText';
import { SourceIconsFan } from './SourceIconsFan';
import { SourcesList } from './SourcesList';
import { TraceStepIcon } from './TraceStepIcon';

/**
 * Tool name (as emitted by the backend on `RESEARCH_STEP_TITLE` / `RESEARCH_STEP_TITLE_SHORT`)
 * for which we want to swap the substep preview text with a friendlier label and prepend
 * that same label as the first substep when the trace is expanded.
 */
const EXECUTE_BASH_TOOL_NAME = 'execute_bash';

const substepIconMaskStyle = cssUnbounded({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	backgroundColor: token('elevation.surface'),
	paddingTop: token('space.025'),
	paddingBottom: token('space.025'),
	position: 'relative',
});

const TWG_TOOL_NAME = 'TeamworkAgent';

// Absolutely-positioned div that renders the TWG grid background across the full step height
// Using a real DOM element (not ::before/::after) since cssMap doesn't support pseudo-elements
// Left offset of 32px skips the icon column (24px icon + 8px gap) so grid only shows on content side
// Uses CSS repeating-linear-gradient with design tokens instead of an SVG file so that
// grid lines adapt to dark mode automatically.
const twgGridStyle = cssUnbounded({
	position: 'absolute',
	inset: 0,
	left: '32px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundColor: token('elevation.surface.sunken'),
	backgroundSize: '26px 26px',
	// Shift the tile origin up by a quarter tile (7px) so the first row appears as a partial
	// cell — the top of the visible area cuts through the middle of a cell instead of starting
	// at a grid line (which would look like a border).
	backgroundPosition: '0 -7px',
	// CSS-only grid using design tokens — adapts to both light and dark mode
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundImage: `repeating-linear-gradient(to right, ${token('color.border')} 0px, ${token('color.border')} 1px, transparent 1px, transparent 26px), repeating-linear-gradient(to bottom, ${token('color.border')} 0px, ${token('color.border')} 1px, transparent 1px, transparent 26px)`,
	// Fade in from left to right
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 90%, black 100%)',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	maskImage: 'linear-gradient(to right, transparent 0%, black 90%, black 100%)',
	pointerEvents: 'none',
	borderRadius: token('radius.large'),
	maxWidth: '600px',
	zIndex: 0,
});

const styles = cssMap({
	stepListItem: {
		// Override global li + li margin-top that adds unwanted spacing between steps
		marginTop: token('space.0'),
		// Override Confluence editor global li styles that add bullet points (e.g. in mini modal)
		listStyle: 'none',
	},
	twgStepListItem: {
		position: 'relative',
		overflow: 'hidden',
		isolation: 'isolate',
	},
	substepIconContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		// Align icon to first line of text (approx half line-height offset)
		paddingTop: token('space.025'),
		width: '24px',
		flexShrink: 0,
		alignSelf: 'stretch',
	},
	substepIconContainerStretch: {
		paddingTop: token('space.0'),
	},
	verticalRuleContinuation: {
		marginTop: token('space.0'),
		width: '0px',
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		flex: 1,
	},
	verticalRuleShort: {
		marginTop: token('space.050'),
		width: '0px',
		minHeight: '8px',
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		flex: 1,
	},
	stepContentWrapper: {
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
		columnGap: token('space.100'),
		alignItems: 'stretch',
		position: 'relative',
	},
	substepRow: {
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
		maxWidth: '600px',
		columnGap: token('space.100'),
		alignItems: 'stretch',
		position: 'relative',
	},
	stepTitleContainer: {
		paddingTop: token('space.100'),
		marginBottom: token('space.100'),
		width: '100%',
		maxWidth: '600px',
	},
	stepTitleContainerWithIcons: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		overflow: 'hidden',
	},
	twgTitleContent: {
		flex: 1,
		minWidth: '0px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	twgIconFanContainer: {
		flexShrink: 0,
		marginLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
	substepContent: {
		flex: 1,
		minWidth: '0px',
		paddingBottom: token('space.050'),
	},
	sourcesContent: {
		marginBottom: token('space.100'),
	},
});

type ResearchStepItemListProps = {
	step: CompactResearchStep;
	isLastItem: boolean;
	isInProgress: boolean;
	hideIcon?: boolean;
	/** When true, renders plain text title instead of shimmer animation even when isInProgress is true */
	disableShimmer?: boolean;
	/** When true, keeps substep content collapsed regardless of isInProgress state */
	forceSubstepCollapsed?: boolean;
	/** When forceSubstepCollapsed is true, renders this text as a single-line preview below the title */
	previewSubstep?: string;
	/** Font size for the step title (default: 'medium' / 14px) */
	titleSize?: 'small' | 'medium';
	/** Font size for the preview substep (default: 'small' / 12px) */
	previewSubstepSize?: 'small' | 'medium';
	/** Font weight for the step title (default: 'regular') */
	titleWeight?: 'regular' | 'medium';
	/**
	 * A mutable ref token representing an expand request. When non-null and
	 * `.current === true`, the item auto-expands and sets `.current = false`
	 * (consumed). Mutation avoids re-renders; spent tokens are naturally
	 * ignored on remount.
	 */
	expandRequest?: React.MutableRefObject<boolean> | null;
	/**
	 * Called when the user explicitly clicks to expand or collapse the
	 * substep expandable. Allows the parent to persist the expand state
	 * (e.g. across preview → completed transitions).
	 */
	onUserExpandToggle?: (stepId: string, expanded: boolean) => void;
	renderSubstep: (substep: string, isStreamed?: boolean) => React.ReactNode;
	onTraceInteraction?: (event: TraceInteractionEvent) => void;
	/** Optional function to modify/wrap each source URL before it is rendered. */
	xpcWrapUrl?: (url: string, stripOnly: boolean) => string;
};

export const ResearchStepItemListRefresh = ({
	step,
	isLastItem,
	isInProgress,
	hideIcon = false,
	disableShimmer = false,
	forceSubstepCollapsed = false,
	previewSubstep,
	titleSize = 'medium',
	previewSubstepSize = 'small',
	titleWeight = 'regular',
	expandRequest,
	onUserExpandToggle,
	renderSubstep,
	onTraceInteraction,
	xpcWrapUrl,
}: ResearchStepItemListProps): JSX.Element => {
	const [isSubstepExpanded, setIsSubstepExpanded] = useState(false);

	// Consume the expand request token: if `.current` is true, auto-expand
	// and mark as consumed. Mutation of `.current` is silent (no re-render),
	// and spent tokens (`.current === false`) are naturally ignored on remount.
	useEffect(() => {
		if (expandRequest?.current) {
			expandRequest.current = false;
			setIsSubstepExpanded(true);
		}
	}, [expandRequest]);

	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);
	const [iconError, setIconError] = useState(false);
	const isTWGIconEnabled = fg('rovo_chat_enable_twg_trace_team26');
	const cyclingIcon = useCyclingIcon(isTWGIconEnabled ? undefined : step.sourceIcons, isInProgress);
	const activeLoadingIcon = cyclingIcon ?? step.toolIcon;
	const title = step.title;

	// For execute_bash steps: replace the raw command/output snippet with a friendlier
	// label, both in the collapsed preview row and as the FIRST substep when expanded.
	const isExecuteBash = step.toolName === EXECUTE_BASH_TOOL_NAME;
	const isTwgStep = isTWGIconEnabled && step.toolName === TWG_TOOL_NAME;
	const executeBashSubstepLabel = formatMessage(messages.executeBashSubstep);
	const effectivePreviewSubstep = isExecuteBash ? executeBashSubstepLabel : previewSubstep;
	const effectiveSeparatedContent = isExecuteBash
		? [executeBashSubstepLabel, ...step.separatedContent]
		: step.separatedContent;
	const hasSubsteps =
		effectiveSeparatedContent.length > 0 ||
		(step.documents !== undefined && step.documents.length > 0);

	const stepTitleShimmer =
		typeof title === 'string' ? <ShimmerText weight={titleWeight}>{title}</ShimmerText> : title;

	const stepTitleNormal =
		typeof title === 'string' ? (
			<Text size={titleSize} weight={titleWeight} color="color.text.subtlest">
				{title}
			</Text>
		) : (
			title
		);

	const showSubsteps = hasSubsteps && isSubstepExpanded;

	return (
		<Box
			as="li"
			xcss={cx(styles.stepListItem, isTwgStep && styles.twgStepListItem)}
			testId="research-step-list-item"
		>
			{isTwgStep && <div css={twgGridStyle} />}
			{/* Main step row */}
			<Box
				xcss={styles.stepContentWrapper}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				{!hideIcon && (
					<TraceStepIcon
						isInProgress={isInProgress}
						toolIcon={step.toolIcon}
						isTwg={isTwgStep}
						activeLoadingIcon={activeLoadingIcon}
						iconError={iconError}
						onIconError={() => setIconError(true)}
					/>
				)}
				<Box xcss={cx(styles.stepTitleContainer, isTwgStep && styles.stepTitleContainerWithIcons)}>
					<Box xcss={isTwgStep ? styles.twgTitleContent : undefined}>
						{hasSubsteps ? (
							<React.Fragment>
								<ResearchExpandable
									defaultExpanded={
										isSubstepExpanded || (forceSubstepCollapsed ? false : isInProgress)
									}
									showDownRightIcons={true}
									layout="inline"
									hideExpandIcon={!isHovered}
									title={isInProgress && !disableShimmer ? stepTitleShimmer : stepTitleNormal}
									onExpandChanged={(expanded) => setIsSubstepExpanded(expanded)}
									onExpandClick={(expanded) => {
										onTraceInteraction?.({ type: TRACE_INTERACTION_TYPES.SINGLE_TRACE, expanded });
										onUserExpandToggle?.(step.stepId, expanded);
									}}
								>
									{/* Empty — substeps rendered below as paired rows */}
									<React.Fragment></React.Fragment>
								</ResearchExpandable>
								{forceSubstepCollapsed && effectivePreviewSubstep && !isSubstepExpanded && (
									<Text size={previewSubstepSize} color="color.text.subtlest" maxLines={1}>
										{stripMarkdown(effectivePreviewSubstep)}
									</Text>
								)}
							</React.Fragment>
						) : isInProgress && !disableShimmer ? (
							stepTitleShimmer
						) : (
							stepTitleNormal
						)}
					</Box>
					{isTwgStep && (
						<Box xcss={styles.twgIconFanContainer}>
							<SourceIconsFan
								icons={step.sourceIcons}
								isInProgress={isInProgress}
								stepId={step.stepId}
							/>
						</Box>
					)}
				</Box>
			</Box>

			{/* Connector from trace icon to first substep — only for last/single step with no sources */}
			{showSubsteps &&
				isLastItem &&
				!hideIcon &&
				!(step.documents && step.documents.length > 0) && (
					<Box xcss={styles.substepRow}>
						<Box xcss={cx(styles.substepIconContainer, styles.substepIconContainerStretch)}>
							<Box xcss={styles.verticalRuleContinuation} />
						</Box>
						<Box xcss={styles.substepContent} />
					</Box>
				)}
			{/* Substep rows — each substep is a paired row with NodeIcon aligned to first line */}
			{step.documents && step.documents.length > 0 && showSubsteps && (
				<Box xcss={styles.substepRow}>
					{!hideIcon && (
						<Box xcss={cx(styles.substepIconContainer, styles.substepIconContainerStretch)}>
							<Box xcss={styles.verticalRuleContinuation} />
						</Box>
					)}
					<Box xcss={cx(styles.substepContent, styles.sourcesContent)}>
						<SourcesList
							sources={step.documents.map((doc) => ({
								title: doc.title,
								url: doc.url,
								iconUrl: doc.product || undefined,
							}))}
							xpcWrapUrl={xpcWrapUrl}
						/>
					</Box>
				</Box>
			)}
			{showSubsteps &&
				effectiveSeparatedContent.map((sub, index) => {
					const isLastSubstep = index === effectiveSeparatedContent.length - 1;
					return (
						<React.Fragment key={`${step.stepId}-substep-${index}`}>
							<Box xcss={styles.substepRow}>
								{!hideIcon && (
									<Box xcss={styles.substepIconContainer}>
										<div css={substepIconMaskStyle}>
											<NodeIcon label="" />
										</div>
										{/* Vertical rule connects to next substep or next step */}
										{(!isLastSubstep || !isLastItem) && <Box xcss={styles.verticalRuleShort} />}
									</Box>
								)}
								<Box xcss={styles.substepContent}>
									{renderSubstep(sub, step.isStreamed ?? false)}
								</Box>
							</Box>
						</React.Fragment>
					);
				})}
		</Box>
	);
};
